import { batch, computed, signal } from '@preact/signals-react'

import { AGGREGATION_PRICE } from '../@constants'
import {
    acriss,
    bookingRange,
    categories,
    currency,
    dailyPrice,
    dateRange,
    fuelPolicy,
    groupBy,
    howToGroup,
    insurance,
    inTerminal,
    isPreviousComparison,
    lengthRental,
    mileage, officeGroup,
    offices,
    officesPickoff,
    prepaid,
    providers, siteGroup,
    sites,
    tiers,
    timeToCompare,
    transmission,
    webSuppliers,
    yearlyComp,
} from '@signals'
import { formCapture, formPickup, rangeFromForm } from '../@services/formDates'
import { mergeLabels } from '../@services/snapshot'
import { goalMissing } from '../@services/goals'


export const BIFilters = signal({})
export const BITableData = signal({})
export const BIChartData = signal({})
export const BIAggregationPrice = signal(AGGREGATION_PRICE.MIN)
export const BIShowByPrice = signal(AGGREGATION_PRICE.MIN)
export const BIIsLoadingChart = signal(false)
export const BIIsLoadingTable = signal(false)
export const BIShowChartLegend = signal(false)
export const BIChartLegendVisibility = signal([])
export const BIOpenUrlModal = signal(null)
export const BIUrlId = signal(null)
export const BIUrlOptions = signal([])
export const BISelectedOffice = signal(null)
export const BISelectedOfficeOptions = signal([])
export const BIIs3DTable = signal(false)
export const BIHeaders = signal([])
export const BISubHeaders = signal([])
export const BI3DTableData = signal({})
export const BIGroupingHeader = signal(null)
// the questions of Advanced (BI) (CU-869fmk5ge piece 1): open or not, the one being answered, its "why" unfolded
export const BIGoalsOpen = signal(false)
export const BIActiveGoal = signal(null)
export const BIWhyOpen = signal(true)
// what the results answer: the form as it was posted, its names, the specific office and the phase (CU-869fmk5ge D3)
export const BIAsked = signal(null)
// the names the filters loaded, `{ field: { id: name } }`: the sentence never prints an id (CU-869fmk5ge T2)
export const BIOptionLabels = signal({})
export const rememberOptions = field => options => {
	BIOptionLabels.value = mergeLabels(BIOptionLabels.value, field, options)
}


export const clearBISignals = () => batch(() => {
	BIFilters.value = {}
	BITableData.value = {}
	BIChartData.value = {}
	BIIsLoadingChart.value = false
	BIIsLoadingTable.value = false
	BIShowChartLegend.value = false
	BIChartLegendVisibility.value = []
})


export const BIFormData = computed(() => ({
    siteGroupID: siteGroup.value,
	siteID: sites.value,
	officePickupID: offices.value,
    officeGroupID: officeGroup.value,
	officeDropoffID: officesPickoff.value,
	// an empty pick-up stays empty (the schema asks for it); an empty capture is today, as before (CU-869fmk5ge D8)
	...formPickup(dateRange.value),
	...formCapture(bookingRange.value),
	lor: lengthRental.value,
	currency: currency.value,
	isPreviousComparison: isPreviousComparison.value,
	periodComp: timeToCompare.value ? {
		type: yearlyComp.value ? 'year' : 'day',
		quantity: timeToCompare.value,
	} : {},
	reportType: howToGroup.value,
	tiers: tiers.value,
	providerID: providers.value,
	webSupplierID: webSuppliers.value,
	categoryID: categories.value,
	acriss: acriss.value,
	unlimited_km: mileage.value,
	insurance: insurance.value,
	is_prepaid: prepaid.value,
	fuelPolicyID: fuelPolicy.value,
	automatic: transmission.value,
	in_terminal_pickup: inTerminal.value,
	isDailyPrice: dailyPrice.value,
	groupBy: groupBy.value,
	isNewVersion: true,
	urlId: BIUrlId.value,
	officePickupIDAux: BISelectedOffice.value
}))

// the fields the question being answered still asks to choose: they block every search (CU-869fmk5ge)
export const BIGoalMissing = computed(() => BIActiveGoal.value ? goalMissing(BIActiveGoal.value.choose, BIFormData.value) : [])

export const setFormData = info => {
	batch(() => {
        siteGroup.value = info.siteGroupID
		sites.value = info.siteID
		offices.value = info.officePickupID
        officeGroup.value = info.officeGroupID
		officesPickoff.value = info.officeDropoffID
		// a link without dates opens with the field empty, never an Invalid Date (CU-869fmk5ge D8)
		dateRange.value = rangeFromForm(info.startPickupDate, info.endPickupDate)
		bookingRange.value = rangeFromForm(info.startBookingDate, info.endBookingDate)
		lengthRental.value = info.lor
		currency.value = info.currency
		isPreviousComparison.value = info.isPreviousComparison
		timeToCompare.value = info.periodComp?.quantity || null
		yearlyComp.value = info.periodComp?.type === 'year'
		howToGroup.value = info.reportType
		tiers.value = info.tiers
		providers.value = info.providerID
		webSuppliers.value = info.webSupplierID
		categories.value = info.categoryID
		acriss.value = info.acriss
		mileage.value = info.unlimited_km
		insurance.value = info.insurance
		prepaid.value = info.is_prepaid
		fuelPolicy.value = info.fuelPolicyID
		transmission.value = info.automatic
		inTerminal.value = info.in_terminal_pickup
		dailyPrice.value = info.isDailyPrice
		groupBy.value = info.groupBy
		BIUrlId.value = info.urlId
	})
}
